import { ReactNode, useState } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { ClientRateconRevision, RateconRevisionOverrides, RateconRevisionRouteStop } from '@numeo/types-main-web-app'
import { Button, Modal } from '@/components/ds2'
import { useAppDispatch } from '@/hooks'
import { updateSingleLoad } from 'store/loadsSlice'
import { getServerErrorMessage } from 'utils/get-user-error-message'
import { showSuccess, showWarning } from 'utils/snackbar'
import { AcceptedRateconRevision, rateconRevisionsApi } from '../api/ratecon-revisions.api'
import { BillingDraft, EMPTY_BILLING_DRAFT, formatSignedUsd, formatUsd, needsBilling, resolveBilling } from '../utils/revisionAdjustments'
import { RateconRevisionBillingPanel } from './RateconRevisionBillingPanel'
import {
    DiffRow,
    isAlreadyResolvedError,
    RateconRevisionDiffTable,
    RateconRevisionDocumentNotice,
    RateconRevisionKindBadges,
    RevisionNote,
} from './RateconRevisionDiff'
import { RateconRevisionLoadSearchOption, RateconRevisionLoadSelector } from './RateconRevisionLoadSelector'
import { RateconRevisionRouteComparison, RateconRevisionRouteShapeNotice } from './RateconRevisionRouteComparison'

/** Long enough to copy the appointments out of: it names what the dispatcher now has to enter by hand. */
const UNAPPLIED_WARNING_MS = 15000

/**
 * The primary "apply" button's words, by what accepting does to the load: reverting to an earlier rate
 * confirmation reads as moving back ("Apply earlier version"), a rate change as applying that rate, and a
 * schedule-only change as applying the revision. "Accept" is avoided — the button applies the change to the
 * load; a separate charge, not this button, is what bills the broker.
 */
export const revisionApplyLabel = (revision: Pick<ClientRateconRevision, 'appliesOlderVersion' | 'changedFields'>): string =>
    revision.appliesOlderVersion ? 'Apply earlier version' : revision.changedFields.includes('rate') ? 'Apply revised rate' : 'Apply revision'

interface RateconRevisionModalProps {
    revision: ClientRateconRevision
    onClose: () => void
    onResolved: (loadId: string) => void
    /** Place in the review queue, e.g. `{ current: 2, total: 5 }`. Omitted when reviewing a single
     *  revision outside the banner's queue. */
    position?: { current: number; total: number }
    /** Step through the queue without acting. Omitted at the respective end of the queue. */
    onPrev?: () => void
    onNext?: () => void
    loadSearchOptions?: RateconRevisionLoadSearchOption[]
    onLoadSearchSelect?: (revisionId: string) => void
}

const ROUTE_FIELD_KEY_REGEX = /^route\[(\d+)\]\.(date|time|arrivalDate|deadlineDate)$/
const ROUTE_LOCATION_KEY_REGEX = /^route\[(\d+)\]\.location$/

const formatCurrency = (amount: number) => `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0 })}`

const ROUTE_FIELD_LABELS: Record<'date' | 'time' | 'arrivalDate' | 'deadlineDate', string> = {
    date: 'date',
    time: 'time',
    arrivalDate: 'arrival date',
    deadlineDate: 'deadline',
}

/** Facility first, then the postal address — the same order the route comparison uses, so a stop
 *  reads the same wherever it appears in this modal. */
const formatStopAddress = (stop?: RateconRevisionRouteStop): string => {
    if (!stop) return '—'
    const place = [stop.city, stop.state].filter(Boolean).join(', ')
    return [stop.facility, stop.street, place, stop.zip].filter(Boolean).join(' · ') || '—'
}

/** arrivalDate/deadlineDate arrive over the wire as ISO strings (JSON has no Date type) despite the shared type saying `Date | null` — format defensively. */
export const formatRouteFieldValue = (value: string | Date | null | undefined): string => {
    if (!value) return '—'
    if (typeof value === 'string') return value
    return new Date(value).toLocaleString()
}

/** Narrowed to just the fields the diff needs — lets the Timeline reuse this against raw log
 *  metadata (v1/v2/changedFields) without needing a full ClientRateconRevision (id, status, etc.). */
export const buildDiffRows = (revision: Pick<ClientRateconRevision, 'v1' | 'v2' | 'changedFields'>): DiffRow[] => {
    const rows: DiffRow[] = []

    if (revision.changedFields.includes('rate')) {
        const delta = revision.v2.rate - revision.v1.rate
        rows.push({
            key: 'rate',
            label: 'Rate',
            original: formatCurrency(revision.v1.rate),
            revised: formatCurrency(revision.v2.rate),
            delta: delta === 0 ? '—' : `${delta > 0 ? '+' : '-'}${formatCurrency(Math.abs(delta))}`,
            deltaTone: delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral',
            editable: true,
        })
    }

    revision.changedFields.forEach((field) => {
        const locationMatch = field.match(ROUTE_LOCATION_KEY_REGEX)
        if (!locationMatch) return
        const stopIndex = Number(locationMatch[1])
        const stopLabel = revision.v1.route[stopIndex]?.type === 'pickup' ? 'Pickup' : 'Delivery'
        rows.push({
            key: field,
            label: `${stopLabel} address`,
            original: formatStopAddress(revision.v1.route[stopIndex]),
            revised: formatStopAddress(revision.v2.route[stopIndex]),
            delta: 'relocated',
            deltaTone: 'neutral',
            editable: false,
        })
    })

    revision.changedFields.forEach((field) => {
        const match = field.match(ROUTE_FIELD_KEY_REGEX)
        if (!match) return
        const stopIndex = Number(match[1])
        const routeField = match[2] as 'date' | 'time' | 'arrivalDate' | 'deadlineDate'
        const stopLabel = revision.v1.route[stopIndex]?.type === 'pickup' ? 'Pickup' : 'Delivery'
        rows.push({
            key: field,
            label: `${stopLabel} ${ROUTE_FIELD_LABELS[routeField]}`,
            original: formatRouteFieldValue(revision.v1.route[stopIndex]?.[routeField]),
            revised: formatRouteFieldValue(revision.v2.route[stopIndex]?.[routeField]),
            delta: 'moved',
            deltaTone: 'neutral',
            editable: true,
        })
    })

    return rows
}

/** The editable (unformatted) form of a changed field's v2 value — date/time are already plain
 *  strings on the wire; arrivalDate/deadlineDate go through ISO so the value re-parses reliably
 *  regardless of the viewer's locale (formatRouteFieldValue's toLocaleString() would not). */
const getEditableValue = (revision: Pick<ClientRateconRevision, 'v1' | 'v2'>, key: string): string => {
    if (key === 'rate') return String(revision.v2.rate)
    const match = key.match(ROUTE_FIELD_KEY_REGEX)
    if (!match) return ''
    const stopIndex = Number(match[1])
    const routeField = match[2] as 'date' | 'time' | 'arrivalDate' | 'deadlineDate'
    const value = revision.v2.route[stopIndex]?.[routeField]
    if (!value) return ''
    return typeof value === 'string' ? value : new Date(value).toISOString()
}

/** A titled block of the review. Plain: the sections are told apart by their headings and spacing, not by boxes. */
const Section = ({ title, aside, children, testId }: { title: string; aside?: ReactNode; children: ReactNode; testId?: string }) => (
    <section className="mt-5 first:mt-1" data-testid={testId}>
        <div className="mb-2 flex items-center gap-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">{title}</h3>
            {aside}
        </div>
        {children}
    </section>
)

export const RateconRevisionModal = ({
    revision,
    onClose,
    onResolved,
    position,
    onPrev,
    onNext,
    loadSearchOptions = [],
    onLoadSearchSelect,
}: RateconRevisionModalProps) => {
    const dispatch = useAppDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isEditing, setIsEditing] = useState(false)
    const [editValues, setEditValues] = useState<Record<string, string>>({})
    const [billingDraft, setBillingDraft] = useState<BillingDraft>(EMPTY_BILLING_DRAFT)
    const [showStops, setShowStops] = useState(false)
    // On an invoiced load the charge is suggested by default; "Add as charge" reveals the fields when there is no suggestion.
    const [chargeRevealed, setChargeRevealed] = useState(false)
    // Accepting a revised rate on an already-submitted invoice without billing the difference is confirmed first.
    const [confirmingAcceptOnly, setConfirmingAcceptOnly] = useState(false)

    // Stepping to another revision in the queue keeps this component mounted (remounting would
    // replay the entrance animation and make the dialog jump), so per-revision state has to be
    // cleared here or an edit-merge draft would carry onto the next load. Done during render
    // rather than in an effect so the incoming revision never paints with the previous one's
    // draft values first — https://react.dev/learn/you-might-not-need-an-effect
    const [renderedRevisionId, setRenderedRevisionId] = useState(revision.id)
    if (renderedRevisionId !== revision.id) {
        setRenderedRevisionId(revision.id)
        setIsEditing(false)
        setEditValues({})
        setBillingDraft(EMPTY_BILLING_DRAFT)
        setShowStops(false)
        setChargeRevealed(false)
        setConfirmingAcceptOnly(false)
        setError(null)
    }

    const diffRows = buildDiffRows(revision)
    const isFinancial = revision.changedFields.includes('rate')

    // On an invoiced load the dispatcher decides what to bill. The panel derives its figure from the rate accepting will write,
    // so an edit-merged rate moves the difference with it instead of leaving a figure computed from the revised one.
    const invoiceContext = revision.invoiceContext
    // Reverting to an earlier rate confirmation version bills nothing: the load moves back and the invoice is left as it was. No charge
    // is offered and no confirmation is asked — it is a deliberate move back, not a forgotten bill.
    const isRevert = revision.appliesOlderVersion === true
    // Only an invoice that went to a factor, or was paid, can take a charge. One that has not is corrected in place.
    const billingContext = invoiceContext?.mode === 'adjust' && !isRevert ? invoiceContext : undefined
    const inAdjust = !!billingContext
    const editedRate = isEditing ? Number(String(editValues.rate ?? '').trim()) : NaN
    const effectiveRate = editedRate > 0 ? editedRate : revision.v2.rate
    const billingResolution = billingContext ? resolveBilling(billingDraft, billingContext, isFinancial, effectiveRate) : null
    // A difference is suggested as a charge by default; otherwise the charge fields appear only once the dispatcher asks.
    const chargeSuggested = !!billingContext && needsBilling(billingContext, isFinancial, effectiveRate)
    const showCharge = inAdjust && (chargeSuggested || chargeRevealed)
    const chargeOk = !!(billingResolution?.ok && billingResolution.adjustment)
    const chargeAmount = billingResolution?.ok ? billingResolution.adjustment.amount : 0
    // Accepting a submitted invoice's load at a new rate while billing nothing is the risky move — it leaves the invoice at the old
    // amount. Only then is a confirmation asked; a schedule-only revision, or one whose rate matches what was billed, is not risky.
    const riskyAcceptOnly = !!billingContext && needsBilling(billingContext, isFinancial, effectiveRate)
    const unbilledDifference = billingContext ? Math.round((effectiveRate - billingContext.billedAmount) * 100) / 100 : 0
    // An adjustment already waiting, on a load whose rate now matches what was billed: accepting only withdraws it.
    const withdrawnAdjustment = inAdjust && !showCharge && isFinancial ? billingContext?.openAdjustment : undefined

    // The adjustment argument accept carries: a decision when charging, an explicit null ("bill nothing") when accepting
    // only on an invoiced-and-factored load, and nothing at all when the load is not invoiced or is corrected in place.
    const adjustmentArg = (wantCharge: boolean): Pick<RateconRevisionOverrides, 'adjustment'> | undefined => {
        if (!inAdjust) return undefined
        if (wantCharge && billingResolution?.ok) return { adjustment: billingResolution.adjustment }
        return { adjustment: null }
    }

    const accept = (overrides: RateconRevisionOverrides) =>
        Object.keys(overrides).length > 0 ? rateconRevisionsApi.accept(revision.id, overrides) : rateconRevisionsApi.accept(revision.id)

    const valueFor = (row: DiffRow): string => editValues[row.key] ?? getEditableValue(revision, row.key)

    const startEditMerge = () => {
        setEditValues(Object.fromEntries(diffRows.filter((row) => row.editable).map((row) => [row.key, valueFor(row)])))
        setIsEditing(true)
    }

    const cancelEditMerge = () => {
        setIsEditing(false)
        setEditValues({})
    }

    const buildOverrides = (): RateconRevisionOverrides => {
        const overrides: RateconRevisionOverrides = {}
        diffRows.forEach((row) => {
            // An address row carries no input, so `valueFor` would read its formatted display text
            // and submit that as an override of a field the backend does not accept.
            if (!row.editable) return
            const value = valueFor(row)
            if (row.key === 'rate') {
                // Number('') is 0 and isNaN(0) is false, so a cleared field used to submit an
                // override of 0 — which the backend reads as a deliberate rate and writes to
                // totalRate/originalRate, invoicing and factoring the load at nothing. An empty or
                // non-positive rate is not an edit; leave the field out and let v2 stand.
                const parsed = Number(String(value).trim())
                if (String(value).trim() !== '' && !isNaN(parsed) && parsed > 0) overrides.rate = parsed
                return
            }
            const match = row.key.match(ROUTE_FIELD_KEY_REGEX)
            if (!match) return
            const stopIndex = Number(match[1])
            const routeField = match[2] as 'date' | 'time' | 'arrivalDate' | 'deadlineDate'
            // An emptied appointment field is not an edit either. The backend takes date/time
            // overrides verbatim, so submitting '' would leave a booked stop with no appointment
            // time — which the ETA engine, the driver templates and the Timeline all render blank.
            if (String(value).trim() === '') return
            overrides.route = { ...overrides.route, [stopIndex]: { ...overrides.route?.[stopIndex], [routeField]: value } }
        })
        return overrides
    }

    const resolveWith = async (action: () => Promise<void>, failureMessage: string) => {
        setIsSubmitting(true)
        setError(null)
        try {
            await action()
        } catch (err) {
            if (isAlreadyResolvedError(err)) {
                // Nothing left to accept/reject — close out to the now-current list instead of
                // leaving a dead modal open with a "try again" that would fail identically.
                onResolved(revision.loadId)
                return
            }
            setError(getServerErrorMessage(err) ?? failureMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    const finishAccept = ({ load, warning, adjustment }: AcceptedRateconRevision) => {
        // The accept endpoint already returns the post-update load — push it straight into the
        // loads slice (the pattern used across this feature, e.g. DriverIdleTimeWarning,
        // useStatusChange, useRefreshETA) so the Ongoing Loads table reflects the new rate/dates
        // immediately instead of showing stale data until a manual page reload.
        dispatch(updateSingleLoad(load))
        // Accepted, with appointments the load's current stops could not take. Said as a toast rather
        // than kept in the modal: the modal closes, and the dispatcher must still see it.
        if (warning) showWarning(warning, UNAPPLIED_WARNING_MS)
        // Said by name: the adjustment is not on this load's row, it is in Ready to Invoice, and that is where the dispatcher goes next.
        if (adjustment)
            showSuccess(
                `${formatSignedUsd(adjustment.amount)} queued in Ready to Invoice for load ${adjustment.loadNumber} (adjusts ${adjustment.originalInvoiceNumber})`
            )
        onResolved(revision.loadId)
    }

    const handleAcceptCharge = () =>
        resolveWith(async () => {
            finishAccept(await accept({ ...adjustmentArg(true) }))
        }, 'Failed to accept with the charge. Please try again.')

    const handleAcceptOnly = () =>
        resolveWith(async () => {
            finishAccept(await accept({ ...adjustmentArg(false) }))
        }, 'Failed to accept revision. Please try again.')

    const handleSaveAndAccept = () =>
        resolveWith(async () => {
            finishAccept(await accept({ ...buildOverrides(), ...adjustmentArg(showCharge) }))
        }, 'Failed to save and accept. Please try again.')

    const handleKeepOriginal = () =>
        resolveWith(async () => {
            await rateconRevisionsApi.reject(revision.id)
            onResolved(revision.loadId)
        }, 'Failed to keep original. Please try again.')

    return (
        <Modal
            open
            onClose={() => !isSubmitting && onClose()}
            size="lg"
            data-testid="ratecon-revision-modal"
            footer={
                confirmingAcceptOnly ? (
                    <div className="flex w-full flex-col gap-2" data-testid="ratecon-accept-only-confirm">
                        <RevisionNote tone="danger">
                            <strong className="text-foreground">{billingContext?.originalInvoiceNumber}</strong> was already sent to the broker or a factor.
                            Accepting without a charge moves the load to the revised rate but leaves the invoice at{' '}
                            {formatUsd(billingContext?.billedAmount ?? 0)} — the broker may pay the old amount. This is recorded on the load’s timeline.
                        </RevisionNote>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                variant="danger"
                                size="md"
                                onClick={() => {
                                    setConfirmingAcceptOnly(false)
                                    handleAcceptOnly()
                                }}
                                disabled={isSubmitting}
                                data-testid="ratecon-accept-only-confirm-btn"
                            >
                                Accept without billing {formatSignedUsd(unbilledDifference)}
                            </Button>
                            <Button variant="secondary" size="md" onClick={() => setConfirmingAcceptOnly(false)} disabled={isSubmitting}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex w-full flex-col gap-2">
                        {error && (
                            <p role="alert" className="text-xs text-danger" data-testid="ratecon-revision-error">
                                {error}
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                            {isEditing ? (
                                <>
                                    <Button variant="primary" size="md" onClick={handleSaveAndAccept} disabled={isSubmitting || (showCharge && !chargeOk)}>
                                        Save &amp; accept
                                    </Button>
                                    <Button variant="secondary" size="md" onClick={cancelEditMerge} disabled={isSubmitting}>
                                        Cancel edit
                                    </Button>
                                    <span className="ml-auto text-xs text-foreground-muted">Edit any merged value; unedited fields keep v2.</span>
                                </>
                            ) : (
                                <>
                                    {showCharge ? (
                                        <>
                                            <Button
                                                variant="primary"
                                                size="md"
                                                onClick={handleAcceptCharge}
                                                disabled={isSubmitting || !chargeOk}
                                                data-testid="ratecon-accept-charge"
                                            >
                                                Charge{chargeOk ? ` ${formatSignedUsd(chargeAmount)}` : ''} &amp; accept
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="md"
                                                onClick={() => (riskyAcceptOnly ? setConfirmingAcceptOnly(true) : handleAcceptOnly())}
                                                disabled={isSubmitting}
                                                data-testid="ratecon-accept-only"
                                            >
                                                Apply rate only
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                variant="primary"
                                                size="md"
                                                onClick={handleAcceptOnly}
                                                disabled={isSubmitting}
                                                data-testid="ratecon-accept-only"
                                            >
                                                {revisionApplyLabel(revision)}
                                            </Button>
                                            {inAdjust && (
                                                <Button
                                                    variant="outline"
                                                    size="md"
                                                    onClick={() => setChargeRevealed(true)}
                                                    disabled={isSubmitting}
                                                    data-testid="ratecon-add-charge"
                                                >
                                                    Add as charge
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    <Button variant="secondary" size="md" onClick={handleKeepOriginal} disabled={isSubmitting}>
                                        Keep original
                                    </Button>
                                    {diffRows.length > 0 && (
                                        <Button variant="outline" size="md" onClick={startEditMerge} disabled={isSubmitting}>
                                            Edit-merge…
                                        </Button>
                                    )}
                                    {position && position.current < position.total && (
                                        <span className="ml-auto text-xs text-foreground-muted">then {position.total - position.current} more</span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )
            }
            header={
                <div className="flex flex-col gap-2 border-b border-border pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold text-foreground">Review revision · Load {revision.rateConfDocumentId}</h2>
                        <div className="flex-1" />
                        <RateconRevisionKindBadges revision={revision} />
                    </div>

                    {/* Queue stepper — only when several revisions are waiting, so a lone revision
                        keeps the simpler header. */}
                    {position && position.total > 1 && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-foreground-muted">
                                Revision {position.current} of {position.total}
                            </span>
                            <div className="flex-1" />
                            <Button
                                variant="tertiary"
                                size="sm"
                                onClick={onPrev}
                                disabled={!onPrev || isSubmitting}
                                prefix={<ChevronLeft size={14} strokeWidth={1.5} />}
                            >
                                Previous
                            </Button>
                            <Button
                                variant="tertiary"
                                size="sm"
                                onClick={onNext}
                                disabled={!onNext || isSubmitting}
                                suffix={<ChevronRight size={14} strokeWidth={1.5} />}
                            >
                                Next
                            </Button>
                        </div>
                    )}

                    <RateconRevisionLoadSelector
                        currentRevisionId={revision.id}
                        options={loadSearchOptions}
                        totalRevisions={position?.total}
                        disabled={isSubmitting}
                        onSelect={onLoadSearchSelect}
                    />
                </div>
            }
        >
            <Section title="Changes">
                <RateconRevisionDiffTable
                    revision={revision}
                    rows={diffRows}
                    isEditing={isEditing}
                    editValues={editValues}
                    onEditValue={(key, value) => setEditValues((prev) => ({ ...prev, [key]: value }))}
                />
            </Section>

            {/* Whose paperwork this actually is. Only ever set on a hand-filed document, and only when it names a different load: reviewing it
                then means deciding whether to move THIS load's rate and stops onto what ANOTHER load's confirmation says. */}
            <RateconRevisionDocumentNotice revision={revision} />

            {/* The stops, whole, when they no longer line up. Only then: for an ordinary revision the field-level diff above says everything.
                The notice is the message; the table is one click away, so it does not push the billing and the buttons off the screen. */}
            {revision.routeShapeChanged && (
                <Section
                    title="Route"
                    aside={
                        <button
                            type="button"
                            onClick={() => setShowStops((open) => !open)}
                            aria-expanded={showStops}
                            data-testid="ratecon-revision-toggle-stops"
                            className="ml-auto inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-accent hover:underline"
                        >
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showStops ? 'rotate-180' : ''}`} aria-hidden="true" />
                            {showStops ? 'Hide stops' : 'Compare stops'}
                        </button>
                    }
                >
                    <RateconRevisionRouteShapeNotice revision={revision} />
                    {showStops && (
                        <div className="mt-2 max-h-[240px] overflow-y-auto">
                            <RateconRevisionRouteComparison v1Route={revision.v1.route} v2Route={revision.v2.route} />
                        </div>
                    )}
                </Section>
            )}

            {/* A locked driver statement that will hold back the stops this revision moves — said before the dispatcher accepts. */}
            {revision.payrollRouteLock && (
                <div className="mt-3" data-testid="ratecon-revision-route-lock-notice">
                    <RevisionNote tone="danger">
                        {revision.payrollRouteLock.affectsMiles
                            ? 'A driver on this load is paid by the mile and the statement is locked, so changing the stops would change the settled miles and pay — the stop changes won’t be applied. The rate still updates.'
                            : 'This load is on a locked payroll statement, so the stop changes won’t be applied. The rate still updates.'}
                    </RevisionNote>
                </div>
            )}

            {withdrawnAdjustment && (
                <div className="mt-3" data-testid="ratecon-revision-withdraw-notice">
                    <RevisionNote>
                        The {formatUsd(withdrawnAdjustment.amount)} adjustment waiting on this load is withdrawn: the rate no longer differs from what was
                        invoiced.
                    </RevisionNote>
                </div>
            )}

            {/* A revert: applying a version older than the one the load is on. It moves the load back and bills nothing. */}
            {isRevert && invoiceContext && (
                <div className="mt-3" data-testid="ratecon-revision-revert-notice">
                    <RevisionNote>
                        This applies an earlier rate confirmation — a move back to a previous version. The load returns to that rate; the invoice is left as it
                        was and no charge is billed.
                    </RevisionNote>
                </div>
            )}

            {showCharge && billingContext && billingResolution && (
                <Section title="Charge">
                    <RateconRevisionBillingPanel
                        context={billingContext}
                        draft={billingDraft}
                        onChange={setBillingDraft}
                        rateChanged={isFinancial}
                        effectiveRate={effectiveRate}
                        resolution={billingResolution}
                        disabled={isSubmitting}
                    />
                </Section>
            )}

            {!billingContext && invoiceContext && !isRevert && (
                <Section title="Invoice">
                    <RevisionNote testId="ratecon-revision-correct-notice">
                        <strong className="text-foreground">{invoiceContext.originalInvoiceNumber}</strong> has not gone to a factor yet. Accepting corrects it
                        to the revised rate, with the revised rate confirmation attached. Nothing is billed separately.
                    </RevisionNote>
                </Section>
            )}
        </Modal>
    )
}
